<script setup lang="ts">
import { useVModel } from "@vueuse/core";
import { ref, reactive } from "vue";
import * as yup from "yup";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import TranslateNamespace from "addeus-common-library/components/base/translation/TranslateNamespace.vue";
import { useCollection } from "addeus-common-library/stores/firestore";
import { Owner } from "../../models/owner";
import functions from "addeus-common-library/stores/function";
import HomeIcon from "../../assets/website/contact/contact-icon-1.svg";
import KartIcon from "../../assets/website/contact/contact-icon-2.svg";
import { useTranslate } from "addeus-common-library/stores/translate";
import { useNotyf } from "addeus-common-library/composable/useNotyf";

const props = defineProps<{ open: boolean; block: string }>();
const emit = defineEmits(["update:open"]);
const open = useVModel(props, "open", emit);

const { translate } = useTranslate();
const notif = useNotyf();

const owners = useCollection(Owner, {
    wheres: [["position", "!=", ""]],
});
const formSchema = yup.object({
    owner: yup.string().required("required"),
    firstName: yup.string().required("required"),
    lastName: yup.string().required("required"),
    email: yup.string().email("email").required("required"),
    phoneNumber: yup
        .string()
        .required("required")
        .matches(/^\+(?:[0-9] ?){6,14}[0-9]$/, "phoneNumber"),
    enterprise: yup.string().required("required"),
    message: yup.string().required("required"),
});

const breakpoints = useBreakpoints(breakpointsTailwind);
const smallerThan = breakpoints.smallerOrEqual("lg");
// import SignupVideo from "/@src/assets/video/signup.mp4";

const isLoading = ref(false);

const form = reactive({
    owner: undefined,
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    enterprise: "",
    message: "",
});

async function handle() {
    isLoading.value = true;
    try {
        await functions.sendEmailContactEventUsV2(form);
    } catch (error) {
        notif.error(translate("speed2max.contactForm.error").value);
    }
    isLoading.value = false;
    open.value = false;
}
</script>

<template>
    <VModal :open="open" size="large" @close="open = false">
        <template #content>
            <TranslateNamespace path="speed2max.formContact">
                <VFlex flex-direction="column" class="p-6">
                    <div class="px-6">
                        <form @submit.prevent="handle">
                            <VValidation
                                v-slot="{ field: formFields }"
                                v-model="form"
                                property="signup"
                                :schema="formSchema">
                                <div class="submit-form">
                                    <TranslateNamespace path=".personnalInformation">
                                        <div
                                            class="is-size-4 is-weight-600 is-uppercase has-text-dark mb-5">
                                            <img
                                                alt="home icon"
                                                :src="HomeIcon"
                                                style="
                                                    height: 30px;
                                                    margin: 0 10px 0 0;
                                                " />
                                            <Translate>.title</Translate>
                                        </div>
                                        <VFlex
                                            :flex-direction="
                                                smallerThan ? 'column' : 'row'
                                            "
                                            justify-content="space-between"
                                            column-gap="1rem"
                                            align-items="start">
                                            <VValidation
                                                v-slot="{ field }"
                                                v-model="form.lastName"
                                                class="w-100"
                                                property="lastName"
                                                :schema="formSchema.fields.lastName">
                                                <VField class="mb-5">
                                                    <VLabel>
                                                        <Translate
                                                            >.lastName.label</Translate
                                                        >
                                                    </VLabel>
                                                    <VControl
                                                        :has-error="
                                                            field.errors.length > 0 &&
                                                            field.isDirty
                                                        ">
                                                        <VInput
                                                            v-model="field.value"
                                                            placeholder=".lastName.placeholder"
                                                            type="text"
                                                            autocomplete="lastName" />
                                                    </VControl>
                                                    <p
                                                        v-for="error in field.errors"
                                                        :key="error"
                                                        class="help is-danger">
                                                        <Translate
                                                            >.lastName.validation.{{
                                                                error
                                                            }}</Translate
                                                        >
                                                    </p>
                                                </VField>
                                            </VValidation>

                                            <VValidation
                                                v-slot="{ field }"
                                                v-model="form.firstName"
                                                property="firstName"
                                                class="w-100"
                                                :schema="formSchema.fields.firstName">
                                                <VField class="mb-5">
                                                    <VLabel>
                                                        <Translate
                                                            >.firstName.label</Translate
                                                        >
                                                    </VLabel>
                                                    <VControl
                                                        :has-error="
                                                            field.errors.length > 0 &&
                                                            field.isDirty
                                                        ">
                                                        <VInput
                                                            v-model="field.value"
                                                            placeholder=".firstName.placeholder"
                                                            type="text"
                                                            autocomplete="firstName" />
                                                    </VControl>
                                                    <p
                                                        v-for="error in field.errors"
                                                        :key="error"
                                                        class="help is-danger">
                                                        <Translate
                                                            >.firstName.validation.{{
                                                                error
                                                            }}</Translate
                                                        >
                                                    </p>
                                                </VField>
                                            </VValidation>
                                        </VFlex>
                                        <VValidation
                                            v-slot="{ field }"
                                            v-model="form.email"
                                            property="email"
                                            class="w-100"
                                            :schema="formSchema.fields.email">
                                            <VField class="mb-5">
                                                <VLabel>
                                                    <Translate>.email.label</Translate>
                                                </VLabel>
                                                <VControl
                                                    :has-error="
                                                        field.errors.length > 0 &&
                                                        field.isDirty
                                                    ">
                                                    <VInput
                                                        v-model="field.value"
                                                        placeholder=".email.placeholder"
                                                        type="text"
                                                        autocomplete="email" />
                                                </VControl>
                                                <p
                                                    v-for="error in field.errors"
                                                    :key="error"
                                                    class="help is-danger">
                                                    <Translate
                                                        >.email.validation.{{
                                                            error
                                                        }}</Translate
                                                    >
                                                </p>
                                            </VField>
                                        </VValidation>

                                        <VValidation
                                            v-slot="{ field }"
                                            v-model="form.phoneNumber"
                                            property="phoneNumber"
                                            class="w-100"
                                            :schema="formSchema.fields.phoneNumber">
                                            <VField class="mb-5">
                                                <VLabel>
                                                    <Translate
                                                        >.phoneNumber.label</Translate
                                                    >
                                                </VLabel>
                                                <VControl
                                                    :has-error="
                                                        field.errors.length > 0 &&
                                                        field.isDirty
                                                    ">
                                                    <VTelInput
                                                        v-model="field.value"
                                                        placeholder=".phoneNumber.placeholder"
                                                        autocomplete="phoneNumber" />
                                                </VControl>
                                                <p
                                                    v-for="error in field.errors"
                                                    :key="error"
                                                    class="help is-danger">
                                                    <Translate
                                                        >.phoneNumber.validation.{{
                                                            error
                                                        }}</Translate
                                                    >
                                                </p>
                                            </VField>
                                        </VValidation>
                                    </TranslateNamespace>
                                    <TranslateNamespace path=".global">
                                        <div
                                            class="is-size-4 is-weight-600 is-uppercase mt-6 mb-5 has-text-dark">
                                            <img
                                                alt="kart icon"
                                                :src="KartIcon"
                                                style="
                                                    height: 30px;
                                                    margin: 0 10px 0 0;
                                                " />
                                            <Translate>.welcome</Translate>
                                        </div>
                                        <VFlex
                                            flex-direction="column"
                                            justify-content="space-between"
                                            align-items="start">
                                            <VValidation
                                                v-slot="{ field }"
                                                v-model="form.owner"
                                                property="owner"
                                                class="w-100"
                                                :schema="formSchema.fields.owner">
                                                <VField class="mb-5">
                                                    <VLabel>
                                                        <Translate
                                                            >.owner.label</Translate
                                                        >
                                                    </VLabel>
                                                    <VControl
                                                        :has-error="
                                                            field.errors.length > 0 &&
                                                            field.isDirty
                                                        ">
                                                        <VSelect v-model="field.value">
                                                            <VOption
                                                                v-for="owner in owners"
                                                                :key="owner.$getID()"
                                                                :value="owner.$getID()"
                                                                >{{ owner.name }}</VOption
                                                            >
                                                        </VSelect>
                                                    </VControl>
                                                    <p
                                                        v-for="error in field.errors"
                                                        :key="error"
                                                        class="help is-danger">
                                                        <Translate
                                                            >.owner.validation.{{
                                                                error
                                                            }}</Translate
                                                        >
                                                    </p>
                                                </VField>
                                            </VValidation>

                                            <VValidation
                                                v-slot="{ field }"
                                                v-model="form.enterprise"
                                                property="enterprise"
                                                class="w-100"
                                                :schema="formSchema.fields.enterprise">
                                                <VField class="mb-5">
                                                    <VLabel>
                                                        <Translate
                                                            >.enterprise.label</Translate
                                                        >
                                                    </VLabel>
                                                    <VControl
                                                        :has-error="
                                                            field.errors.length > 0 &&
                                                            field.isDirty
                                                        ">
                                                        <VInput
                                                            v-model="field.value"
                                                            placeholder=".enterprise.placeholder"
                                                            autocomplete="enterprise" />
                                                    </VControl>
                                                    <p
                                                        v-for="error in field.errors"
                                                        :key="error"
                                                        class="help is-danger">
                                                        <Translate
                                                            >.enterprise.validation.{{
                                                                error
                                                            }}</Translate
                                                        >
                                                    </p>
                                                </VField>
                                            </VValidation>

                                            <VValidation
                                                v-slot="{ field }"
                                                v-model="form.message"
                                                property="message"
                                                class="w-100"
                                                :schema="formSchema.fields.message">
                                                <VField class="mb-5">
                                                    <VLabel>
                                                        <Translate
                                                            >.message.label</Translate
                                                        >
                                                    </VLabel>
                                                    <VControl
                                                        :has-error="
                                                            field.errors.length > 0 &&
                                                            field.isDirty
                                                        ">
                                                        <VTextarea
                                                            v-model="field.value"
                                                            :placeholder="
                                                                translate(
                                                                    'speed2max.formContact.global.message.placeholder',
                                                                ).value
                                                            " />
                                                    </VControl>
                                                    <p
                                                        v-for="error in field.errors"
                                                        :key="error"
                                                        class="help is-danger">
                                                        <Translate
                                                            >.message.validation.{{
                                                                error
                                                            }}</Translate
                                                        >
                                                    </p>
                                                </VField>
                                            </VValidation>
                                        </VFlex>
                                    </TranslateNamespace>
                                    <VControl class="signup">
                                        <VButton
                                            id="signup-button"
                                            :disabled="
                                                formFields.hasChildErrors() ||
                                                !formFields.isDirty
                                            "
                                            :loading="isLoading"
                                            color="primary"
                                            type="submit"
                                            bold
                                            fullwidth
                                            raised>
                                            <Translate path=".submit"></Translate
                                        ></VButton>
                                    </VControl>
                                </div>
                            </VValidation>
                        </form>
                    </div>
                </VFlex>
            </TranslateNamespace>
        </template>
    </VModal>
</template>

<style lang="scss">
.v-modal {
    .vue-tel-input > .vti__dropdown {
        top: 8px;
    }

    input {
        height: 50px !important;
    }

    .w-100 {
        width: 100%;
    }

    button > span {
        width: 100%;
    }

    .form-icon {
        padding-top: 10px;
    }

    > .modal-background {
        border: 0;
    }

    > .modal-content {
        > .modal-card {
            > .modal-card-head {
                border-bottom: 0;
            }

            > .modal-card-body {
                padding-top: 0;

                > .inner-content {
                    > div {
                        > .copy-block {
                            max-width: 450px;
                        }
                    }
                }
            }
        }
    }
}
</style>
